import React, { useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useGpsFeature } from '@/hooks/use-company-features';
import { useTripsComparisonList, TripsComparisonFilters } from '@/hooks/use-trips-comparison-list';
import { TripComparisonCard } from '@/components/fleet/TripComparisonCard';
import { TripComparisonModal } from '@/components/fleet/TripComparisonModal';
import { TripComparison } from '@/hooks/use-trip-comparison';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Filter, RefreshCw, BarChart3, CheckCircle2, XCircle, Target } from 'lucide-react';

export default function TripsAnalysisPage() {
  const { activeCompany } = useAuth();
  const { hasAccess: hasGPS } = useGpsFeature(activeCompany?.id);

  const [filters, setFilters] = useState<TripsComparisonFilters>({
    status: 'all'
  });
  const [selectedComparison, setSelectedComparison] = useState<TripComparison | null>(null);

  const { data: comparisons, isLoading, refetch } = useTripsComparisonList(
    activeCompany?.id,
    filters
  );

  // Verificar se GPS está ativo (FleetONE já é requisito)
  if (!hasGPS) {
    return (
      <div className="p-6">
        <Alert>
          <AlertDescription>
            <p className="font-semibold mb-2">Este recurso requer GPS ativo no FleetONE.</p>
            <div className="text-destructive">❌ GPS / Rastreamento não está ativo</div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const tripsWithGPS = comparisons?.filter(c => c.gps_trip_id).length || 0;
  const tripsWithoutGPS = comparisons?.filter(c => !c.gps_trip_id).length || 0;
  const avgAdherence = comparisons?.length && tripsWithGPS > 0
    ? (comparisons
        .filter(c => c.adherence_score)
        .reduce((acc, c) => acc + (c.adherence_score || 0), 0) / tripsWithGPS)
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Análise de Viagens</h1>
          <p className="text-muted-foreground">
            Compare viagens planejadas com viagens realizadas
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Viagens
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{comparisons?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Viagens planejadas
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Com GPS
            </CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {tripsWithGPS}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Viagens detectadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sem GPS
            </CardTitle>
            <XCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {tripsWithoutGPS}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Aguardando execução
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Aderência Média
            </CardTitle>
            <Target className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              avgAdherence >= 70 ? 'text-green-600' : 
              avgAdherence >= 50 ? 'text-yellow-600' : 
              'text-red-600'
            }`}>
              {avgAdherence.toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Score de aderência
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Trips Grid */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
          <p className="mt-2 text-muted-foreground">Carregando comparativos...</p>
        </div>
      ) : comparisons && comparisons.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {comparisons.map((comparison) => (
            <TripComparisonCard
              key={comparison.fleet_trip_id}
              comparison={comparison}
              onViewDetails={() => setSelectedComparison(comparison)}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              Nenhuma viagem planejada encontrada para análise
            </p>
          </CardContent>
        </Card>
      )}

      {/* Modal */}
      <TripComparisonModal
        open={!!selectedComparison}
        onOpenChange={(open) => !open && setSelectedComparison(null)}
        comparison={selectedComparison}
      />
    </div>
  );
}
